import { createFileRoute } from '@tanstack/react-router'
import { forwardRef, useEffect, useRef, useState, type FormEvent } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Check, HeartHandshake, ExternalLink, AlertTriangle, X, HelpCircle } from 'lucide-react'
import { searchCharities, type Charity } from '@/lib/api'
import {
  assessDonationRequest,
  type Assessment,
  type Channel,
  type Payment,
  type YesNoUnsure,
} from '@/lib/donation-request'

export const Route = createFileRoute('/tools/charity-checker')({
  component: CharityCheckerPage,
})

/**
 * Two questions, kept visibly separate, in the order Lee's prototype puts
 * them: (1) is this a real registered organization, answered from IRS
 * records; (2) does the donation request in front of me look like it came
 * from it, answered from what the user tells us plus what we can observe.
 *
 * Styling follows the prototype measure for measure (18px radius, shield
 * status icon, tinted org card, white request card, tinted result, three
 * rules), with the prototype's blues as violet and navy as #221a3d.
 *
 * Honesty note: we do not hold official charity websites yet, so the
 * prototype's "does the link match the official domain" signal cannot run.
 * The Official website row says so and offers a web search instead.
 */

const SHIELD = '[clip-path:polygon(50%_0,94%_14%,90%_66%,50%_100%,10%_66%,6%_14%)]'
const SHIELD_ICON = '-translate-y-[6px] sm:-translate-y-[8px]'
const NAVY = 'text-[#221a3d]'

const CHANNELS: { value: Channel; label: string }[] = [
  { value: 'phone', label: 'Phone call' },
  { value: 'text', label: 'Text / WhatsApp' },
  { value: 'email', label: 'Email' },
  { value: 'social', label: 'Social media' },
  { value: 'doorstep', label: 'At the door' },
  { value: 'website', label: 'A website I found' },
]

const PAYMENTS: { value: Payment; label: string }[] = [
  { value: 'not_asked', label: 'They haven’t said yet' },
  { value: 'card_on_call', label: 'Card details' },
  { value: 'link_they_sent', label: 'A link they sent' },
  { value: 'bank_transfer', label: 'Bank transfer' },
  { value: 'official_site_myself', label: 'Official website' },
  { value: 'check', label: 'A check' },
  { value: 'gift_card', label: 'Gift card' },
  { value: 'crypto', label: 'Cryptocurrency' },
  { value: 'wire', label: 'Wire transfer' },
  { value: 'app_to_person', label: 'Zelle / Venmo / Cash App to a person' },
  { value: 'cash_courier', label: 'Cash' },
]

const YNU: { value: YesNoUnsure; label: string }[] = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
  { value: 'unsure', label: 'Not sure' },
]

function formatEin(ein: string): string {
  const d = ein.replace(/\D/g, '').padStart(9, '0')
  return `${d.slice(0, 2)}-${d.slice(2)}`
}

function money(n?: number): string | null {
  if (n == null || !Number.isFinite(n) || n <= 0) return null
  if (n >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(1)}bn`
  if (n >= 1_000_000) return `$${Math.round(n / 1_000_000)}m`
  if (n >= 1_000) return `$${Math.round(n / 1_000)}k`
  return `$${n}`
}

function searchUrl(name: string) {
  return `https://www.google.com/search?q=${encodeURIComponent(`${name} official website donate`)}`
}

function CharityCheckerPage() {
  const [input, setInput] = useState('')
  const [query, setQuery] = useState('')
  const [selected, setSelected] = useState<Charity | null>(null)

  const { data, isFetching, isError } = useQuery({
    queryKey: ['charity-search', query],
    queryFn: () => searchCharities(query),
    enabled: query.trim().length >= 3,
    retry: 0,
    staleTime: 5 * 60 * 1000,
  })
  const results = data?.results ?? []
  const searched = query.trim().length >= 3 && !isFetching

  // One match: select it. Several: let the user pick.
  useEffect(() => {
    if (searched && results.length === 1) setSelected(results[0])
  }, [searched, results])

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    setSelected(null)
    setQuery(input.trim())
  }

  const orgVerified = selected !== null

  return (
    <div className="bg-gradient-to-b from-white to-[#fcfbff]">
      <main className="mx-auto max-w-[1180px] px-4 pb-10 pt-[22px] sm:px-7 sm:pb-16 sm:pt-[46px]">
        {/* Hero */}
        <section className="mb-7 grid grid-cols-1 items-center gap-4 sm:grid-cols-[72px_1fr] lg:grid-cols-[104px_1fr] lg:gap-[26px]">
          <div
            aria-hidden
            className={`hidden h-[72px] w-[64px] place-items-center bg-gradient-to-br from-violet-500 to-violet-800 text-white sm:grid lg:h-[98px] lg:w-[88px] ${SHIELD}`}
          >
            <HeartHandshake className="h-8 w-8 -translate-y-[2px] lg:h-11 lg:w-11 lg:-translate-y-[3px]" strokeWidth={2.25} />
          </div>
          <div>
            <h1 className={`m-0 mb-[10px] text-[36px] font-bold leading-[1.03] tracking-[-1.4px] sm:text-[40px] lg:text-[54px] lg:tracking-[-2px] ${NAVY}`}>
              Charity Checker
            </h1>
            <p className="m-0 max-w-[760px] text-[16px] leading-[1.55] text-[#2f2a4a] sm:text-[18px]">
              Check whether a charity is registered and whether the donation request looks legitimate.
            </p>
            <div className="mt-[13px] flex items-center gap-2 text-[15px] font-bold text-violet-700">
              <Check className="h-4 w-4" strokeWidth={3} />
              <span>We use official IRS charity records.</span>
            </div>
          </div>
        </section>

        {/* Search card */}
        <form
          onSubmit={onSubmit}
          className="mb-5 rounded-[18px] border border-[#e6e2f3] bg-white p-5 shadow-[0_10px_30px_rgba(56,23,112,.07)]"
        >
          <label htmlFor="charitySearch" className={`mb-2 ms-1 block text-[14px] font-extrabold ${NAVY}`}>
            Charity name or EIN
          </label>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-[1fr_225px]">
            <input
              id="charitySearch"
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder="e.g. American Red Cross, or 53-0196605"
              className="h-[66px] w-full rounded-[13px] border-[1.5px] border-[#b9a9d8] bg-white px-[18px] text-[18px] text-[#1c1131] outline-none placeholder:text-slate-400 focus:border-violet-600 focus:shadow-[0_0_0_4px_rgba(124,58,237,.08)]"
            />
            <button
              type="submit"
              disabled={input.trim().length < 3 || isFetching}
              className="h-[60px] cursor-pointer rounded-[12px] border-0 bg-gradient-to-b from-violet-600 to-violet-800 text-[18px] font-extrabold text-white shadow-[0_8px_18px_rgba(124,58,237,.2)] disabled:cursor-not-allowed disabled:from-slate-200 disabled:to-slate-200 disabled:text-slate-400 disabled:shadow-none sm:h-auto"
            >
              {isFetching ? 'Checking…' : 'Check charity'}
            </button>
          </div>
          <p className="mx-[3px] mb-0 mt-[9px] text-[13px] text-[#7a7492]">
            Search by name, or by EIN if you have it. US charities only.
          </p>
        </form>

        {isError && (
          <p className="mb-5 text-[16px] text-[#a13b31]">
            We couldn’t reach the charity register just now. Please try again in a moment.
          </p>
        )}

        {/* Several matches: pick one */}
        {searched && results.length > 1 && !selected && (() => {
          const total = data?.totalMatches ?? results.length
          const broad = total > results.length
          return (
          <section className="mb-5 rounded-[18px] border border-[#e6e2f3] bg-white p-6 shadow-[0_10px_30px_rgba(56,23,112,.07)] sm:p-7">
            <h2 className={`m-0 text-[24px] font-bold ${NAVY}`}>
              {broad
                ? `${total >= 10000 ? 'Thousands of' : total.toLocaleString()} organizations match “${data?.query}”. Showing the closest ${results.length}.`
                : `${results.length} organizations match “${data?.query}”. Which one did you mean?`}
            </h2>
            <p className="mb-0 mt-2 text-[16px] leading-[1.5] text-[#5c5a75]">
              {broad ? (
                <>
                  That is too many to be useful, and the one you mean may not be in this list. Add
                  another word from the charity’s name to narrow it, for example{' '}
                  <span className="font-semibold text-[#221a3d]">{data?.query} red cross</span>, or search by EIN.
                </>
              ) : (
                <>Similar names are common. Check the location and size against the charity you have in mind.</>
              )}
            </p>
            <ul className="m-0 mt-5 list-none divide-y divide-[#e6e2f3] border-y border-[#e6e2f3] p-0">
              {results.map(c => (
                <li key={c.registrationNumber}>
                  <button
                    type="button"
                    onClick={() => setSelected(c)}
                    className="flex w-full cursor-pointer flex-wrap items-center justify-between gap-x-6 gap-y-1 border-0 bg-transparent px-1 py-4 text-start hover:bg-violet-50/60"
                  >
                    <span>
                      <span className={`block text-[17px] font-bold ${NAVY}`}>{c.name}</span>
                      <span className="block text-[14px] text-[#5c5a75]">
                        EIN {formatEin(c.registrationNumber)}
                        {(c.city || c.state) && <> · {[c.city, c.state].filter(Boolean).join(', ')}</>}
                        {money(c.income) && <> · {money(c.income)} income</>}
                      </span>
                    </span>
                    <span className="text-[14px] font-extrabold text-violet-700">Select</span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
          )
        })()}

        {/* Organization card */}
        {searched && selected && (
          <OrgCard
            charity={selected}
            others={results.length - 1}
            onOthers={() => setSelected(null)}
          />
        )}
        {searched && results.length === 0 && <NotFoundCard name={data?.query ?? query} />}

        {/* Request check: shown once any search has been made */}
        {searched && (
          <>
            <div className="my-7 flex items-center gap-[14px] text-[14px] font-bold text-[#6f6a8a] before:h-px before:flex-1 before:bg-[#e6e2f3] after:h-px after:flex-1 after:bg-[#e6e2f3]">
              Now check the request itself
            </div>
            <RequestCheck
              organizationName={selected?.name ?? (results.length === 0 ? query : null)}
              organizationVerified={orgVerified}
              official={{ website: selected?.website, websiteHost: selected?.websiteHost, phone: selected?.phone }}
            />
          </>
        )}

        {/* Rules */}
        <section className="mt-[26px] rounded-[18px] border border-[#e6e2f3] bg-white px-[26px] py-6">
          <h2 className={`m-0 mb-4 text-[21px] font-bold ${NAVY}`}>Three simple rules for safer giving</h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <Rule n="1" title="Verify the organization" body="Look for a matching record in an official charity register." />
            <Rule n="2" title="Don’t trust the contact alone" body="A scammer can impersonate a real charity or brand." />
            <Rule n="3" title="Donate independently" body="If you are unsure, find the charity’s official website yourself and donate there." />
          </div>
        </section>

        <p className="mx-auto mb-0 mt-[18px] max-w-[860px] text-center text-[13px] leading-[1.55] text-[#7a7492]">
          Charity records are drawn from official IRS registrations and the annual returns charities file
          with the IRS, and are refreshed monthly. A charity’s website and phone number come from its own
          filing. Financial figures reflect the most recent return on record and may be a year or two
          behind. What we can verify, what you tell us, and what looks suspicious are always kept
          separate.
        </p>
      </main>
    </div>
  )
}

/* ── Organization card ───────────────────────────────────────────── */

function formatPhone(d: string) {
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : d
}

function OrgCard({ charity: c, others, onOthers }: { charity: Charity; others: number; onOthers: () => void }) {
  const address = [c.street, [c.city, c.state].filter(Boolean).join(', '), c.zip].filter(Boolean).join(', ')
  const revoked = c.revoked === true || c.status === 'removed'
  const card = revoked ? 'border-[#eccac4] bg-[#fff4f2]' : 'border-[#cde5c8] bg-[#f5fbf4]'
  return (
    <section className={`mb-5 rounded-[18px] border px-[18px] py-[22px] sm:px-7 sm:py-[26px] ${card}`}>
      <div className="grid grid-cols-[54px_1fr] items-start gap-[13px] sm:grid-cols-[62px_1fr] sm:gap-[18px] md:grid-cols-[70px_1fr_auto]">
        <div className={`grid h-[54px] w-[54px] place-items-center rounded-full text-white sm:h-[62px] sm:w-[62px] ${revoked ? 'bg-[#c9382a]' : 'bg-[#278a2f]'}`}>
          {revoked ? <X className="h-7 w-7 sm:h-8 sm:w-8" strokeWidth={3} /> : <Check className="h-7 w-7 sm:h-8 sm:w-8" strokeWidth={3} />}
        </div>
        <div className="min-w-0">
          <div className={`mb-1 text-[13px] font-extrabold uppercase tracking-[.07em] ${revoked ? 'text-[#a13b31]' : 'text-[#2f8d36]'}`}>
            {revoked ? 'Exemption revoked by the IRS' : 'Organization verified'}
          </div>
          <h2 className={`m-0 mb-[7px] text-[24px] font-bold leading-[1.2] sm:text-[28px] ${NAVY}`}>{c.name}</h2>
          <p className="m-0 text-[16px] text-[#3a3556]">
            {revoked
              ? `The IRS automatically revoked this organization's tax-exempt status${c.revocationDate ? ` on ${c.revocationDate}` : ''} for not filing returns. It may still exist, but it is not currently a registered charity.`
              : `${c.isPublicCharity501c3 ? 'Registered 501(c)(3) public charity' : 'Registered tax-exempt organization'}${c.state ? ` in ${c.state}` : ''}`}
          </p>
        </div>
        <div className="col-start-2 md:col-start-3">
          {revoked ? (
            <span className="inline-flex items-center gap-[7px] rounded-full border border-[#efbbb4] bg-[#fff0ee] px-3 py-2 text-[14px] font-extrabold text-[#9a3026]">
              <X className="h-4 w-4" strokeWidth={3} />
              Revoked
            </span>
          ) : (
            <span className="inline-flex items-center gap-[7px] rounded-full border border-[#b8dab4] bg-[#e9f6e7] px-3 py-2 text-[14px] font-extrabold text-[#22682a]">
              <Check className="h-4 w-4" strokeWidth={3} />
              Registered
            </span>
          )}
        </div>
      </div>

      <div className="mt-[22px] grid grid-cols-1 gap-[15px] border-t border-[#d8e9d5] pt-5 sm:grid-cols-2 sm:gap-[22px] md:ms-[88px] md:grid-cols-3">
        <Detail label="EIN" value={formatEin(c.registrationNumber)} />
        <Detail
          label="Official website"
          value={
            c.website && c.websiteHost ? (
              <a
                href={c.website}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-violet-700 no-underline hover:underline"
              >
                {c.websiteHost} <ExternalLink className="h-3.5 w-3.5" />
              </a>
            ) : (
              <>
                <span className="font-semibold text-[#5c5a75]">Not on record.</span>{' '}
                <a
                  href={searchUrl(c.name)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-violet-700 no-underline hover:underline"
                >
                  Search for it <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </>
            )
          }
        />
        <Detail label="Registered address" value={address || 'Not on record'} />
        <Detail label="Phone on file" value={c.phone ? formatPhone(c.phone) : 'Not on record'} />
        <Detail label="Registered since" value={c.registeredDate ? c.registeredDate.slice(0, 4) : 'Not stated'} />
        <Detail label="Latest filing" value={c.latestFilingYear ? String(c.latestFilingYear) : 'Not stated'} />
        <Detail label="Annual income" value={money(c.income) ?? 'Not stated'} />
      </div>

      {c.filingsLookStale && (
        <p className="mb-0 mt-4 flex gap-2 text-[15px] leading-[1.5] text-[#7a5410] md:ms-[88px]">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          No recent annual filing on record. Active charities file every year, so this is worth asking about before you give.
        </p>
      )}

      <p className="mb-0 mt-[18px] text-[13px] leading-[1.5] text-[#5f6c5b] md:ms-[88px]">
        Source: official IRS records
        {c.website || c.phone ? `. Website and phone are as stated on the charity’s own ${c.contactTaxYear ? c.contactTaxYear + ' ' : ''}annual return` : ''}.{' '}
        <a href={c.sourceUrl} target="_blank" rel="noopener noreferrer" className="text-violet-700 no-underline hover:underline">
          See the full record
        </a>
        . Verifying the charity does <strong>not</strong> verify the person, link or message asking you to donate.
        {others > 0 && (
          <>
            {' '}
            <button type="button" onClick={onOthers} className="cursor-pointer border-0 bg-transparent p-0 text-[13px] font-bold text-violet-700 hover:underline">
              Not the right one? See {others} other {others === 1 ? 'match' : 'matches'}.
            </button>
          </>
        )}
      </p>
    </section>
  )
}

function NotFoundCard({ name }: { name: string }) {
  return (
    <section className="mb-5 rounded-[18px] border border-[#efddb2] bg-[#fff9ee] px-[18px] py-[22px] sm:px-7 sm:py-[26px]">
      <div className="grid grid-cols-[54px_1fr] items-start gap-[13px] sm:grid-cols-[62px_1fr] sm:gap-[18px]">
        <div className="grid h-[54px] w-[54px] place-items-center rounded-full bg-[#f2d997] text-[#765000]">
          <HelpCircle className="h-7 w-7" strokeWidth={2.5} />
        </div>
        <div>
          <div className="mb-1 text-[13px] font-extrabold uppercase tracking-[.07em] text-[#9a6500]">Could not verify</div>
          <h2 className={`m-0 mb-[7px] text-[24px] font-bold leading-[1.2] sm:text-[28px] ${NAVY}`}>{name}</h2>
          <p className="m-0 text-[16px] leading-[1.5] text-[#3a3556]">
            Nothing in IRS records matches that name. That is not proof of a scam: small and very new
            charities may not appear, and the registered name is often longer than the name used in
            appeals. Try the exact registered name, or the EIN if you have it. If someone is pressing you
            to give right now and you cannot find them here, treat that as a warning sign.
          </p>
        </div>
      </div>
    </section>
  )
}

function Detail({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="mb-[5px] text-[12px] font-extrabold uppercase tracking-[.06em] text-[#75816f]">{label}</div>
      <div className="text-[16px] font-bold leading-[1.4] text-[#1e2e49]">{value}</div>
    </div>
  )
}

/* ── Request check ───────────────────────────────────────────────── */

function RequestCheck({
  organizationName,
  organizationVerified,
  official,
}: {
  organizationName: string | null
  organizationVerified: boolean
  official: { website?: string; websiteHost?: string; phone?: string }
}) {
  const [contactGiven, setContactGiven] = useState('')
  const [channel, setChannel] = useState<Channel>('phone')
  const [payment, setPayment] = useState<Payment>('not_asked')
  const [urgency, setUrgency] = useState<YesNoUnsure>('unsure')
  const [discouraged, setDiscouraged] = useState<YesNoUnsure>('unsure')
  const [result, setResult] = useState<Assessment | null>(null)
  const resultRef = useRef<HTMLElement>(null)
  const detailRef = useRef<HTMLInputElement>(null)

  // A new organization means a new request; clear the old verdict.
  useEffect(() => {
    setResult(null)
  }, [organizationName, organizationVerified])

  function run() {
    setResult(
      assessDonationRequest({
        channel,
        contactGiven: contactGiven.trim() || undefined,
        payment,
        urgency,
        discouragedVerifying: discouraged,
        organizationVerified,
        organizationName,
        officialWebsiteHost: official.websiteHost ?? null,
        officialPhone: official.phone ?? null,
      }),
    )
    requestAnimationFrame(() => resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }))
  }

  return (
    <>
      <section className="mb-5 rounded-[18px] border border-[#e6e2f3] bg-white p-[18px] shadow-[0_10px_30px_rgba(56,23,112,.07)] sm:p-7">
        <div className="mb-[22px] flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-5">
          <div>
            <h2 className={`m-0 mb-[7px] text-[24px] font-bold leading-[1.2] sm:text-[28px] ${NAVY}`}>Did someone ask you to donate?</h2>
            <p className="m-0 max-w-[720px] text-[16px] leading-[1.5] text-[#5c5a75]">
              Tell us how they contacted you and how they want you to pay. We’ll check the request
              against common scam warning signs.
            </p>
          </div>
          <span className="inline-block self-start whitespace-nowrap rounded-full bg-violet-50 px-3 py-2 text-[13px] font-extrabold text-violet-700">
            Optional second check
          </span>
        </div>

        <div className="mb-[18px]">
          <label htmlFor="requestDetail" className={`mb-2 ms-[2px] block text-[14px] font-extrabold ${NAVY}`}>
            Website, link, email or phone number they gave you
          </label>
          <input
            ref={detailRef}
            id="requestDetail"
            value={contactGiven}
            onChange={e => setContactGiven(e.target.value)}
            placeholder="Paste a link, email or phone number (optional)"
            className="h-[66px] w-full rounded-[13px] border-[1.5px] border-[#b9a9d8] bg-white px-[18px] text-[18px] text-[#1c1131] outline-none placeholder:text-slate-400 focus:border-violet-600 focus:shadow-[0_0_0_4px_rgba(124,58,237,.08)]"
          />
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-[1.25fr_.75fr]">
          <div className="mb-[18px]">
            <div className={`mb-2 ms-[2px] text-[14px] font-extrabold ${NAVY}`}>How did they contact you?</div>
            <Chips options={CHANNELS} value={channel} onChange={setChannel} />
          </div>
          <div className="mb-[18px]">
            <label htmlFor="payment" className={`mb-2 ms-[2px] block text-[14px] font-extrabold ${NAVY}`}>How did they ask you to pay?</label>
            <select
              id="payment"
              value={payment}
              onChange={e => setPayment(e.target.value as Payment)}
              className="h-[52px] w-full rounded-[13px] border-[1.5px] border-[#b9a9d8] bg-white px-4 text-[16px] text-[#1c1131] outline-none focus:border-violet-600 focus:shadow-[0_0_0_4px_rgba(124,58,237,.08)]"
            >
              {PAYMENTS.map(p => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="mb-[18px]">
            <div className={`mb-2 ms-[2px] text-[14px] font-extrabold ${NAVY}`}>Did they pressure you to act quickly?</div>
            <Chips options={YNU} value={urgency} onChange={setUrgency} />
          </div>
          <div className="mb-[18px]">
            <div className={`mb-2 ms-[2px] text-[14px] font-extrabold ${NAVY}`}>Did they tell you not to check with anyone else?</div>
            <Chips options={YNU} value={discouraged} onChange={setDiscouraged} />
          </div>
        </div>

        <button
          type="button"
          onClick={run}
          className="mt-1 h-[58px] cursor-pointer rounded-[12px] border-0 bg-gradient-to-b from-violet-600 to-violet-800 px-6 text-[18px] font-extrabold text-white shadow-[0_8px_18px_rgba(124,58,237,.2)]"
        >
          Check this donation request
        </button>
      </section>

      {result && (
        <RequestResult
          ref={resultRef}
          result={result}
          organizationName={organizationName}
          organizationVerified={organizationVerified}
          officialWebsite={official.website ?? null}
          contactGiven={contactGiven.trim()}
          onEdit={() => detailRef.current?.focus()}
        />
      )}
    </>
  )
}

function Chips<T extends string>({ options, value, onChange }: { options: { value: T; label: string }[]; value: T; onChange: (v: T) => void }) {
  return (
    <div className="flex flex-wrap gap-[9px]">
      {options.map(o => {
        const active = o.value === value
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(o.value)}
            aria-pressed={active}
            className={`cursor-pointer rounded-full border px-[13px] py-[10px] text-[14px] font-bold ${
              active ? 'border-violet-300 bg-violet-50 text-violet-700' : 'border-[#d3cbe6] bg-white text-[#3a3556]'
            }`}
          >
            {o.label}
          </button>
        )
      })}
    </div>
  )
}

/* ── Request result ──────────────────────────────────────────────── */

const RequestResult = forwardRef<
  HTMLElement,
  { result: Assessment; organizationName: string | null; organizationVerified: boolean; officialWebsite: string | null; contactGiven: string; onEdit: () => void }
>(function RequestResult({ result, organizationName, organizationVerified, officialWebsite, contactGiven, onEdit }, ref) {
  const level = result.level
  const tone =
    level === 'strongly_matches_scam' ? 'danger'
    : level === 'no_obvious_warning_signs' ? 'safe'
    : 'warn'
  const card =
    tone === 'danger' ? 'border-[#eccac4] bg-[#fff4f2]'
    : tone === 'safe' ? 'border-[#cde5c8] bg-[#f5fbf4]'
    : 'border-[#efddb2] bg-[#fff9ee]'
  const icon =
    tone === 'danger' ? 'bg-[#f1d3ce] text-[#8d332a]'
    : tone === 'safe' ? 'bg-[#dcefd9] text-[#28722e]'
    : 'bg-[#f2d997] text-[#765000]'
  const Icon = tone === 'danger' ? X : tone === 'safe' ? Check : level === 'insufficient' ? HelpCircle : AlertTriangle

  const title =
    tone === 'danger'
      ? 'Strong warning signs. Do not use the payment details you were sent.'
      : tone === 'safe'
        ? 'No obvious warning signs found.'
        : level === 'insufficient'
          ? 'Not enough to go on yet.'
          : organizationVerified
            ? 'Charity verified. Donation request not verified.'
            : 'Charity not found. Donation request not verified.'

  const body =
    tone === 'danger'
      ? organizationVerified
        ? 'The charity itself is real, but this request carries several warning signs. If you still want to donate, find the charity’s official website yourself and give there.'
        : 'This request carries several warning signs, and the organization is not on the register. Do not pay.'
      : tone === 'safe'
        ? 'Nothing you told us is a known warning sign. That does not guarantee the request is genuine, so stay cautious if anything changes or feels unusual.'
        : level === 'insufficient'
          ? 'Nothing here is a warning sign yet, but there is not much to go on. If they ask you to pay, come back and tell us how.'
          : organizationVerified
            ? 'The organization is real, but we cannot confirm this request came from it. The safest option is to avoid the details you were sent and contact the charity yourself.'
            : 'We could not find the organization, and we cannot confirm this request. Do not give through it.'

  const signals = result.findings.filter(f => f.severity !== 'info')

  return (
    <section
      ref={ref}
      className={`mb-5 grid grid-cols-1 items-start gap-[18px] rounded-[18px] border px-[18px] py-[22px] sm:grid-cols-[62px_1fr] sm:px-7 sm:py-[25px] ${card}`}
    >
      <div className={`grid h-[46px] w-[46px] place-items-center rounded-full sm:h-[54px] sm:w-[54px] ${icon}`}>
        <Icon className="h-6 w-6 sm:h-7 sm:w-7" strokeWidth={3} />
      </div>
      <div className="min-w-0">
        <h3 className={`m-0 mb-[7px] text-[21px] font-bold leading-[1.25] sm:text-[23px] ${NAVY}`}>{title}</h3>
        <p className="m-0 text-[16px] leading-[1.55] text-[#4a4762]">{body}</p>
        {contactGiven && organizationVerified && !officialWebsite && !result.findings.some(f => (f.provenance === 'observed' || f.provenance === 'verified') && f.severity !== 'info' && !f.id.startsWith('org_')) && (
          <p className="mb-0 mt-2 text-[15px] leading-[1.55] text-[#5c5a75]">
            Nothing about the contact details you pasted is a known warning sign. We don’t hold this
            charity’s official website or phone number, so we can’t confirm they belong to it. Find the
            charity yourself and compare.
          </p>
        )}
        {signals.length > 0 && (
          <ul className="mb-0 mt-[14px] list-disc ps-5 text-[16px] leading-[1.75] text-[#3c3a55]">
            {signals.map(f => (
              <li key={f.id}>{f.text}</li>
            ))}
          </ul>
        )}
        <div className="mt-[18px] flex flex-wrap gap-[10px]">
          {officialWebsite ? (
            <a
              href={officialWebsite}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-[10px] border border-violet-600 bg-violet-600 px-[14px] py-[11px] text-[15px] font-extrabold text-white no-underline hover:bg-violet-700"
            >
              Go to the official charity website
              <ExternalLink className="h-4 w-4" />
            </a>
          ) : organizationName ? (
            <a
              href={searchUrl(organizationName)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-[10px] border border-violet-600 bg-violet-600 px-[14px] py-[11px] text-[15px] font-extrabold text-white no-underline hover:bg-violet-700"
            >
              Find the official charity website
              <ExternalLink className="h-4 w-4" />
            </a>
          ) : null}
          <button
            type="button"
            onClick={onEdit}
            className={`inline-flex cursor-pointer items-center rounded-[10px] border border-[#c9bfe3] bg-white px-[14px] py-[11px] text-[15px] font-extrabold ${NAVY}`}
          >
            Edit this check
          </button>
        </div>
      </div>
    </section>
  )
})

function Rule({ n, title, body }: { n: string; title: string; body: string }) {
  return (
    <div className="flex items-start gap-3">
      <div className="grid h-[38px] w-[38px] min-w-[38px] place-items-center rounded-full bg-violet-50 font-black text-violet-700">{n}</div>
      <div>
        <h3 className={`mb-1 mt-px text-[15px] font-bold ${NAVY}`}>{title}</h3>
        <p className="m-0 text-[13px] leading-[1.45] text-[#66628a]">{body}</p>
      </div>
    </div>
  )
}
